import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import { IActionHandler, OrgInfo, CollectionServerUser } from "@preveil-api";
import {
  AdminMessages, Message, useAppDispatch, useAppSelector, MessageHandlerDisplayType, MessageToastTypes, MessageAnchors,
  useGetOrgInfo, Account, collectionApi, usePutToggleCloudOnlyModeMutation, usePostUsersOrgsByEntityIdAddGlobalSyncExceptionMutation,
  useDeleteUsersOrgsByEntityIdGlobalSyncExceptionsMutation, AdminSuccessMessages,
  AdminErrorMessages, GetCloudOnlyModeStateApiResponse,
  GetUsersOrgsByEntityIdGlobalSyncExceptionsApiResponse
} from "src/common";
import { PageHeader, AlertWithSwitch, Loading, Icon, EmptyView, CoverTemplate } from "src/components";
import { AddUsersModal, Toolbar, ListView } from ".";
import { uiActions, accountActions, adminActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  account: Account;
  org_info: OrgInfo;
}

// Description: https://preveil.atlassian.net/wiki/spaces/ESD/pages/3780313101/Admin+Console+-+Cloud+Lock+Settngs
function CloudLockSettingsComponent(props: AllProps) {
  const { account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const new_global_sync_change = useAppSelector((state: RootState) => state.app.new_global_sync_change);
  const [org_users, setOrgUsers] = useState<CollectionServerUser[]>([]);
  const [cloud_lock_status, setCloudLockStatus] = useState<boolean>(false);
  const [show_modal, setShowModal] = useState<boolean>(false);
  const [filter_param, setFilterParam] = useState<string>("");
  const [exception_user_list, setExceptionUserList] = useState<CollectionServerUser[]>([]);
  const [filtered_list, setFilteredList] = useState<CollectionServerUser[]>([]);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [is_users_loading, setIsUsersLoading] = useState<boolean>(true);
  const [getCloudOnlyOrgLockState] = collectionApi.endpoints.getCloudOnlyOrgLockState.useLazyQuery();
  const [getExceptionUsers] = collectionApi.endpoints.getUsersOrgsByEntityIdGlobalSyncExceptions.useLazyQuery();
  const [putToggleCloudOnlyMode] = usePutToggleCloudOnlyModeMutation();
  const [postUsersOrgsByEntityIdAddGlobalSyncException] = usePostUsersOrgsByEntityIdAddGlobalSyncExceptionMutation();
  const [deleteUsersOrgsByEntityIdGlobalSyncExceptions] = useDeleteUsersOrgsByEntityIdGlobalSyncExceptionsMutation();
  const { data: org_data, error, refresh } = useGetOrgInfo(account, org_info);
  const cloud_lock_message = cloud_lock_status ? AdminMessages.cloud_lock_message.enabled : AdminMessages.cloud_lock_message.disabled;

  useEffect(() => {
    fetchCloudOnlyOrgLockState();
    return refresh();
  }, []);

  // Description: if there is a new global sync change we want to propagate/dispatch those changes through the app (Drive).
  // and update the org cloud state for the other admins.
  useEffect(() => {
    if (new_global_sync_change) {
      fetchCloudOnlyOrgLockState().then(sync => {
        _.isBoolean(sync) && dispatch(accountActions.setGlobalLocalSync(sync));
      });
    }
  }, [new_global_sync_change]);

  useEffect(() => {
    if (!_.isEmpty(org_data) && org_data.org_users) {
      const { org_users: users } = org_data;
      const _org_users = users.filter((user: CollectionServerUser) => {
        // In org_info.users array a deleted user is marked as claimed and deleted
        // so checking just in case at some point org_users also have deleted users.
        return user.claimed && !user.deleted;
      });
      setOrgUsers(_org_users);
      fetchExceptionUsers(_org_users);
    }
    if (error) {
      const params = {
        message: AdminErrorMessages.error_fetching_org_info,
        stack: error
      };
      dispatchPageError(params);
    }
  }, [org_data, error]);

  async function fetchExceptionUsers(_org_users: CollectionServerUser[]) {
    getExceptionUsers({
      account_ids: Account.getAccountIdentifiers(account),
      body: {
        entityId: org_info.org_id
      }
    }).unwrap().then((response: GetUsersOrgsByEntityIdGlobalSyncExceptionsApiResponse) => {
      const _users = _org_users.filter((user: CollectionServerUser) => response.users.includes(user.user_id));
      setExceptionUserList(_users);
      setFilteredList(_users);
    }).catch((error) => {
      dispatchPageError({ message: AdminErrorMessages.error_fetching_sync_permission_list, stack: error });
    }).finally(() => {
      setIsUsersLoading(false);
    });
  }

  // Description: dispatch any error found in the page.
  function dispatchPageError(params: { message: string, stack?: any }) {
    dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
  }

  // Description: Toggle the cloud lock mode on/off
  async function toggleCloudModeLockRequest() {
    const sync = cloud_lock_status; // We already flipped the status at first load so we can just pass it as it is.
    const account_ids = Account.getAccountIdentifiers(account);
    const entityId = org_info.org_id;
    const params = {
      account_ids,
      body: {
        entityId,
        sync
      }
    };
    await putToggleCloudOnlyMode(params)
      .unwrap()
      .then(() => {
        const message = AdminSuccessMessages.success_cloud_lock_setting_change;
        // We don't get any response at this point as to what is the state of the cloud lock (response.sync),
        // so we just flipped the current state again.
        setCloudLockStatus(!sync);
        setIsLoading(false);
        dispatch(uiActions.handleSetMessage(new Message(message)));
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_cloud_lock_setting_change,
          stack: error
        };
        setIsLoading(false);
        dispatchPageError(params);
      });
  }

  // Description: Add users to the global sync exception list
  async function addUsersToCloudLockExceptionList(users: string[]) {
    if (users.length > 0) {
      const message_template = users.length === 1 ? "user" : "users";
      const account_ids = Account.getAccountIdentifiers(account);
      const entityId = org_info.org_id;
      const params = {
        account_ids,
        body: {
          entityId,
          users
        }
      };
      await postUsersOrgsByEntityIdAddGlobalSyncException(params)
        .unwrap()
        .then(() => {
          const message = AdminSuccessMessages.success_add_user_cloud_lock_permission.replace(MessageAnchors.message_content, message_template);
          dispatch(uiActions.handleSetMessage(new Message(message)));
          setIsUsersLoading(true);
          fetchExceptionUsers(org_users);
        })
        .catch((error) => {
          const message = AdminErrorMessages.error_adding_user_to_cloud_lock_permission.replace(MessageAnchors.message_content, message_template);
          const params = {
            message,
            stack: error
          };
          dispatchPageError(params);
        });
    }
  }

  // Description: Remove users from the global sync exception list
  async function removeUsersFromCloudLockExceptionList(users: string[]) {
    if (users.length > 0) {
      const message_template = users.length === 1 ? "user" : "users";
      const account_ids = Account.getAccountIdentifiers(account);
      const entityId = org_info.org_id;
      const params = {
        account_ids,
        body: {
          entityId,
          users
        }
      };
      await deleteUsersOrgsByEntityIdGlobalSyncExceptions(params)
        .unwrap()
        .then(() => {
          const message = AdminSuccessMessages.success_remove_user_cloud_lock_permission.replace(MessageAnchors.message_content, message_template);
          dispatch(adminActions.setSelected([]));
          dispatch(uiActions.handleSetMessage(new Message(message)));
          setIsUsersLoading(true);
          fetchExceptionUsers(org_users);
        })
        .catch((error) => {
          const message = AdminErrorMessages.error_removing_user_from_cloud_lock_permission.replace(MessageAnchors.message_content, message_template);
          const params = {
            message,
            stack: error
          };
          dispatchPageError(params);
        });
    }
  }

  // Description: Fetch the cloud lock state of the organization
  async function fetchCloudOnlyOrgLockState(): Promise<boolean | void> {
    getCloudOnlyOrgLockState({
      account_ids: Account.getAccountIdentifiers(account),
      body: {
        entityId: org_info.org_id
      }
    })
      .unwrap()
      .then((response: GetCloudOnlyModeStateApiResponse) => {
        const { sync } = response;
        setCloudLockStatus(!sync);
        return sync;
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_fetching_cloud_lock_status,
          stack: error
        };
        dispatchPageError(params);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  // Description: Handle the toggle of the cloud lock mode
  function handleToggleCloudModeLock() {
    if (!cloud_lock_status) {
      // Note: we want to confirm with the admin if they want to enable cloud lock
      confirmCloudLockSetting();
    } else {
      // No need to confirm when toggling off the cloud lock.
      toggleCloudModeLockRequest();
    }
  }

  // Description: Confirm the cloud lock setting change, before making the request
  // This is to prevent accidental changes to the cloud lock setting.
  function confirmCloudLockSetting() {
    const confirmation_dialog = new Message(
      AdminMessages.sync_setting_change.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.sync_setting_change.title,
      {
        label: "Yes",
        data: true,
        action: () => toggleCloudModeLockRequest()
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Confirm the deletion of users from the sync permission list
  function confirmDeleteUsers(users: string[]) {
    const message_body = AdminMessages.cloud_lock_remove_user.body.replace(MessageAnchors.message_content, users.length > 1 ? "users" : "user");
    const confirmation_dialog = new Message(
      message_body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.cloud_lock_remove_user.title,
      {
        label: "Yes",
        data: users,
        action: (users: string[]) => removeUsersFromCloudLockExceptionList(users)
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  const CloudLockSettingRequests = {
    handleShowModal: () => {
      setShowModal(!show_modal);
    },
    handleGetRefreshRequest: () => {
      setIsUsersLoading(true);
      fetchExceptionUsers(org_users);
    },
    handleReset: () => {
      setFilterParam("");
    },
    handleSelect: (params: { id?: string, selected: boolean }) => {
      let new_selected = selected.slice();
      if (!!params.id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === params.id)) &&
            new_selected.push(params.id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === params.id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        new_selected = params.selected ? filtered_list.map((user: CollectionServerUser) => user.user_id) : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = exception_user_list.filter((user: CollectionServerUser) => {
          return user.display_name.toLowerCase().includes(param.toLowerCase());
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(exception_user_list);
      }
    },
    handleAddUserIdToSyncPermissionList: (users: string[]) => {
      setShowModal(false);
      addUsersToCloudLockExceptionList(users);
    },
    handleDelete: (params?: { users: string[] }) => {
      const to_delete = params?.users || selected;
      confirmDeleteUsers(to_delete);
    }
  };

  function handlePageActions(actionObj: IActionHandler) {
    const callback = `handle${actionObj.actionType}`;
    if ((CloudLockSettingRequests as any)[callback] instanceof Function) {
      (CloudLockSettingRequests as any)[callback](actionObj.params);
    }
  }

  function RenderUi() {
    if (is_loading) {
      return <Loading className="in-place" />;
    }
    if (cloud_lock_status) {
      return <>
        <h4>Users sync permissions</h4>
        <p>Targeted users can be granted sync permissions below:</p>
        <Toolbar is_loading={is_users_loading} org_users={org_users} user_list={exception_user_list} handleAction={handlePageActions} filter_param={filter_param} filtered_list={filtered_list}/>
        {exception_user_list.length === 0 && !is_users_loading ? 
          <EmptyView message="There are no users with sync permissions."/> : 
          <div id="outer-scope" className="cover-content">
            <ListView user_list={filtered_list} is_loading={is_users_loading} selected={selected} handleAction={handlePageActions}  />
          </div>}
      </>;
    } else {
      return <div className="max-600">
        <Icon className="cloud-lock-image d-block" />
        <p>Set permissions for users to sync or un-sync drive content to their devices.</p>
      </div>;
    }
  }

  return <CoverTemplate className="admin-component">
    <PageHeader>
      <h1>Cloud Lock Settings</h1>
    </PageHeader>
    <Card className="card-section h-100">
      <AlertWithSwitch
        popover={true}
        disabled={is_loading}
        toggleState={cloud_lock_status}
        handleChange={handleToggleCloudModeLock}
        popover_message={cloud_lock_status ? AdminMessages.cloud_lock_message.tooltip_on : AdminMessages.cloud_lock_message.tooltip_off}
      >
        <span dangerouslySetInnerHTML={{ __html: cloud_lock_message }} />
      </AlertWithSwitch>
      {RenderUi()}
      <AddUsersModal
        org_users={org_users.filter((user: CollectionServerUser) => !exception_user_list.includes(user))}
        current_account={account}
        show_modal={show_modal}
        handleAction={handlePageActions}
      />
    </Card>
  </CoverTemplate>;
}

export default React.memo(CloudLockSettingsComponent);